// DashboardStatsAndBarChart.tsx
import React from 'react';
import { Box, Typography, Stack, useTheme, Paper, useMediaQuery } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import AddBoxIcon from '@mui/icons-material/AddBox';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { DashboardSummary, TrendData, DashboardCounts } from '../../../services/dashboardService';
import BarChartSkeleton from './BarChartSkeleton';

const data = [
  { name: '1 JUL', count: 320 },
  { name: '2 JUL', count: 270 },
  { name: '3 JUL', count: 220 },
  { name: '4 JUL', count: 330 },
  { name: '5 JUL', count: 90 },
  { name: '6 JUL', count: 180 },
  { name: '7 JUL', count: 260 },
  { name: '8 JUL', count: 130 },
  { name: '9 JUL', count: 300 },
  { name: '10 JUL', count: 340 },
  { name: '11 JUL', count: 310 },
  { name: '12 JUL', count: 320 },
  { name: '13 JUL', count: 350 },
  { name: '14 JUL', count: 140 },
  { name: '15 JUL', count: 90 },
  { name: '16 JUL', count: 50 },
];

const StatCard = ({ label, value, icon, selected = false, onClick }: any) => {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.5,
        borderRadius: 1.5,
        bgcolor: selected ? '#132F5F' : '#FFF',
        color: selected ? '#fff' : theme.palette.text.primary,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: selected ? '#132F5F' : '#E5E7EB',
        boxShadow: selected ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: selected ? '#132F5F' : '#D1D5DB',
          bgcolor: selected ? '#132F5F' : '#F9FAFB'
        }
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          bgcolor: selected ? '#fff' : '#F3F4F6',
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: selected ? '#132F5F' : 'inherit',
          flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="body2"
          fontSize="14px"
          fontWeight={500}
          color={selected ? '#fff' : '#6B7280'}
          mb={0.5}
        >
          {label}
        </Typography>
        <Typography 
          fontSize="18px" 
          fontWeight={600}
          color={selected ? '#fff' : '#111827'}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

type StatusFilter = 'all' | 'open' | 'approved' | 'rejected';

interface DashboardStatsAndBarChartProps {
  summary: DashboardSummary;
  trend: TrendData[];
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  selectedStatus: StatusFilter;
  onStatusChange: (status: StatusFilter) => void;
  counts?: DashboardCounts;
  isLoading?: boolean;
}

const DashboardStatsAndBarChart: React.FC<DashboardStatsAndBarChartProps> = ({
  summary,
  trend,
  selectedPeriod,
  onPeriodChange,
  selectedStatus,
  onStatusChange,
  counts,
  isLoading = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  const displaySummary: DashboardSummary = counts
    ? {
        total: counts.all,
        open: counts.open,
        approved: counts.approved,
        rejected: counts.rejected,
      }
    : summary;

  return (
    <Paper
      elevation={1}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: "12px",
        bgcolor: '#fff',
        height: '100%'
      }}
    >
      {isLoading ? (
        <BarChartSkeleton />
      ) : (
        <>
          {/* Stats Row */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            mb={4} 
            sx={{
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              '& > *': {
                flex: { xs: '1 1 100%', sm: '1 1 0' },
                minWidth: 'auto'
              }
            }}
          >
            <StatCard
              label="All Indigents"
              value={displaySummary.total.toString()}
              icon={<AddBoxIcon sx={{ color: '#132F5F' }} />}
              selected={selectedStatus === 'all'}
              onClick={() => onStatusChange('all')}
            />
            <StatCard
              label="Open"
              value={displaySummary.open.toString()}
              icon={<InventoryIcon sx={{ color: '#EACC09' }} />}
              selected={selectedStatus === 'open'}
              onClick={() => onStatusChange('open')}
            />
            <StatCard
              label="Fully Approved"
              value={displaySummary.approved.toString()}
              icon={<CheckCircleIcon sx={{ color: '#4CAF50' }} />}
              selected={selectedStatus === 'approved'}
              onClick={() => onStatusChange('approved')}
            />
            <StatCard
              label="Rejected"
              value={displaySummary.rejected.toString()}
              icon={<CancelIcon sx={{ color: '#F44336' }} />}
              selected={selectedStatus === 'rejected'}
              onClick={() => onStatusChange('rejected')}
            />
          </Stack>

          {/* Bar Chart */}
          <Box sx={{ 
              width: '100%', 
              height: { 
                xs: 250, 
                sm: 275,
                md: 300 
              },
              mt: { xs: 2, sm: 3 }
            }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={trend} 
                barSize={isMediumScreen ? 20 : 25}
                margin={{
                  top: 5,
                  right: 10,
                  bottom: 5,
                  left: 10
                }}
                maxBarSize={25}
              >
                <XAxis 
                  dataKey="month" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ 
                    fontSize: isMobile ? 10 : isMediumScreen ? 11 : 12,
                    fill: '#6b7280'
                  }} 
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ 
                    fontSize: isMobile ? 10 : isMediumScreen ? 11 : 12,
                    fill: '#6b7280'
                  }}
                  label={{
                    value: 'INDIGENT COUNT',
                    angle: -90,
                    position: 'insideLeft',
                    style: { 
                      textAnchor: 'middle', 
                      fill: '#9E9E9E', 
                      fontSize: isMobile ? 10 : isMediumScreen ? 11 : 12,
                    },
                  }}
                />
                <Tooltip />
                <Bar dataKey="count" fill="#7890C7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default DashboardStatsAndBarChart;

